<template>
	<label v-if="tabTooltip && isI18nPackageIncluded" :class="attrs.class" class="field-label mb--15">
		<span>Language settings</span>
		<assist-tooltip
			:content="t('tooltip.confirmTranslation')"
			:place="'start'"
			:direction="'bottom'"
			:theme="'dark'"
			:visible="true"
		></assist-tooltip>
	</label>
<div
	:class="modsClassname"
	class="language-control"
>
	<div
		@click="chooseLanguage($event, language)"
		:class="{'active': language?.id === languageCurrent?.id && !disabled}"
		class="control-item"
		aria-label="lang-main-item"
	>
		<span>Language: </span>
		<span class="uppercase">{{ language?.id }}</span>
	</div>
	<template v-if="isI18nPackageIncluded">
		<div
			v-for="lang of languagesChosen"
			:key="lang.id"
			@click="chooseLanguage($event, lang)"
			:class="{active: languageCurrent?.id === lang.id && !disabled}"
			class="control-item"
			aria-label="lang-chosen-item"
		>
			<span class="uppercase">{{ lang.id.substring(0, 2) }}</span>
			<sup v-if="lang.id.length > 2">{{ lang.id.substring(3, 5) }}</sup>
			<i
				:class="translationStatuses[lang.id]?.translated ? 'bg--blue-grey' : 'bg--golden'"
				class="fas fa-language control-item__icon"
			></i>
			<field-checkbox
				:checked="translationStatuses[lang.id]?.translated"
				:use-change-emit="true"
				@change-checked="checkedChange($event, lang.id)"
				:disabled="languageCurrent?.id !== lang.id || disabled"
				:icon-mods="'mr--0'"
				:aria-label="'field-checkbox'"
				class="mb--0 mr--none"
			></field-checkbox>
		</div>
	</template>
</div>
</template>

<script>
import {computed, useAttrs} from 'vue';
import {useStore} from "vuex";
import FieldCheckbox from "@/components/fields/FieldCheckbox.vue";
import AssistTooltip from "@/components/ui/tooltip/AssistTooltip.vue";
import {useI18n} from "vue-i18n";
export default {
	components: {
		FieldCheckbox,
		AssistTooltip
	},
	props: {
		translationStatuses: {
			type: Object,
			default: () => null
		},
		languageCurrent: {
			type: Object,
			default: () => null
		},
		disabled: {
      type: Boolean,
			default: false
		},
		tabTooltip: {
      type: Boolean,
			default: true,
		},
		modsClassname: {
      type: String,
			default: ''
		}
	},
	emits: {
		'update:language-current': () => true,
		'status-changed': () => true,
    'languageChanged': () => true
	},
	setup(props, {emit}) {
		/**
		 * Most of the sections works similar way like this:
		 * when a section is opened, data loading from server
		 * without storing this data in store;
		 * following this idea, the LC component should do two things:
		 * 1. to show translation status of the section
		 * 2. to switch current language of the edited section
		 *
		 * When LC is used in the Panel component, it additionally should provide:
		 * 3. click on control item should open a panel with chosen language
		 *
		 * Props:
		 *  translationStatuses: Object
		 *  languageCurrent: Object
		 *  isPanel: Boolean
		 *
		 * languageDefault and languageAdditional would be getting from store
		 * */
    const attrs = useAttrs();

		const {t} = useI18n();
		const store = useStore();
		const language = computed(() => store.getters['maps/languages/getLanguageComputed']);
		const languagesChosen = computed(() => store.getters['maps/languages/getLanguagesChosenComputed']);
		
		// client package
		const clientFeatures = computed(() => store.getters["clients/getClientFeatures"]);
		const isI18nPackageIncluded = computed(() => clientFeatures.value?.has_i18n);
		
		function chooseLanguage(event, lang){
      if(props.disabled) return;
      event.stopPropagation();
			emit('update:language-current', lang);
      emit('languageChanged');
		}
		
		// checked
		function checkedChange(value, id){
			emit('status-changed', {checked: value, id})
		}
		
		return {
			t,
      attrs,
			language,
			languagesChosen,
			isI18nPackageIncluded,
			chooseLanguage,
			checkedChange,
		}
	}
}
</script>

<style lang="scss">
@use "@/scss/ui/tabs/language-control";
@use "@/scss/ui/tabs/control-item";
</style>